import { registerCellType } from 'handsontable/cellTypes';
import { TextEditor } from 'handsontable/editors';
import type Handsontable from 'handsontable';
import { baseRenderer } from 'handsontable/renderers';

import { Icons } from '@/constants/Icons';
import { _isNil, _notNil, _isNotEmpty } from '@/littledash';
import type { Color } from '@/model/Common.model';
import type { CellValue } from '@/support/hot.types';
import iro from '@jaames/iro';

import './ColorEditor.scss';

const validHexColorPattern = /^#([0-9A-F]{3}){1,2}$/i;

export const colorRenderer = (
  hotInstance: Handsontable,
  td: HTMLTableCellElement,
  row: number,
  column: number,
  prop: string | number,
  value: CellValue,
  cellProperties: Handsontable.CellProperties
): void => {
  baseRenderer(hotInstance, td, row, column, prop, value, cellProperties);

  while (td.firstChild) {
    td.removeChild(td.firstChild);
  }

  if (!td.classList.contains('htInvalid') && typeof value === 'string' && _isNotEmpty(value)) {
    const colorChip = document.createElement('span');
    colorChip.classList.add('color-render-chip');
    colorChip.style.backgroundColor = value;
    td.appendChild(colorChip);
  }
};

export const colorValidator = (value: CellValue, callback: (valid: boolean) => void): void => {
  callback(typeof value === 'string' && validHexColorPattern.test(value));
};

const colorSwatch: Array<Color> = [
  '#333333',
  '#0066CC',
  '#B90745',
  '#F39521',
  '#743789',
  '#4EACF6',
  '#E56C92',
  '#78AD2B',
  '#F6CB45',
  '#00968C',
  '#8E1807',
  '#00007D',
  '#9B6D31',
  '#FF475A',
  '#0BCAB5',
  '#D4E27E',
  '#3E4EE0',
  '#EAC76C',
  '#FFCB90',
  '#90DED6',
  '#E5BFEE',
  '#879CA0',
  '#5F44AD',
  '#C9E4EB',
];

export class ColorEditor extends TextEditor {
  static type = 'color';

  private _colorValue?: string;
  private _isOpened = false;
  private _isDestroyed = false;

  colorPickerContainer?: HTMLDivElement;
  colorPickerModal?: HTMLDivElement;
  colorPickerHeader?: HTMLDivElement;
  colorPickerBody?: HTMLDivElement;
  colorPickerFooter?: HTMLDivElement;
  iconSvg?: SVGSVGElement;
  iconPath?: SVGPathElement;
  colorHexInput?: HTMLInputElement;
  colorHexError?: HTMLParagraphElement;
  colorChip?: HTMLSpanElement;
  saveButton?: HTMLButtonElement;
  cancelButton?: HTMLButtonElement;
  colorPicker?: iro.ColorPicker;

  static register(type = ColorEditor.type): void {
    registerCellType(type, {
      CELL_TYPE: type,
      editor: ColorEditor,
      renderer: colorRenderer,
      validator: colorValidator,
    });
  }

  static swatch: Array<Color> = colorSwatch;

  static randomColor(): Color {
    return `#${((Math.random() * 0xffffff) << 0).toString(16).padStart(6, '0')}`;
  }

  static randomSwatchColor(): Color {
    return ColorEditor.swatch[Math.floor(Math.random() * ColorEditor.swatch.length)];
  }

  static colorForIndex(index: number): Color {
    if (_notNil(ColorEditor.swatch?.[index])) {
      return ColorEditor.swatch[index];
    }
    return ColorEditor.randomColor();
  }

  init(): void {
    super.init();
    this.hot.addHook('afterDestroy', () => this.destroyElements());
  }

  prepare(
    row: number,
    col: number,
    prop: string | number,
    td: HTMLTableCellElement,
    originalValue: unknown,
    cellProperties: Handsontable.CellProperties
  ): void {
    this.row = row;
    this.col = col;
    this.prop = prop;
    this.TD = td;
    this.cellProperties = cellProperties;
    this.originalValue = originalValue;

    if (
      _notNil(originalValue) &&
      typeof originalValue === 'string' &&
      _isNotEmpty(originalValue) &&
      validHexColorPattern.test(originalValue)
    ) {
      this._colorValue = originalValue;
    } else {
      this._colorValue = ColorEditor.colorForIndex(row);
    }
  }

  createElements(): void {
    super.createElements();

    if (_notNil(this.colorPickerContainer)) {
      return;
    }

    const existingContainer = this.hot.rootDocument.querySelector('.color-picker-container');
    if (_notNil(existingContainer)) {
      existingContainer.remove();
    }

    this.colorPickerContainer = this.hot.rootDocument.createElement('div');
    this.colorPickerContainer.classList.add('color-picker-container');
    this.colorPickerContainer.style.display = 'none';
    this.colorPickerContainer.setAttribute('data-test-component', 'ColorEditor');
    this.colorPickerContainer.setAttribute('data-test-element', 'container');
    this.colorPickerContainer.setAttribute('data-test-id', 'color-picker-container');

    this.colorPickerModal = this.hot.rootDocument.createElement('div');
    this.colorPickerModal.classList.add('color-picker-modal');
    this.colorPickerModal.style.top = '0';
    this.colorPickerModal.style.left = '0';
    this.colorPickerContainer.appendChild(this.colorPickerModal);

    this.colorPickerHeader = this.hot.rootDocument.createElement('div');
    this.colorPickerHeader.classList.add('color-picker-header');
    this.colorPickerModal.appendChild(this.colorPickerHeader);

    this.colorPickerBody = this.hot.rootDocument.createElement('div');
    this.colorPickerBody.classList.add('color-picker-body');
    this.colorPickerModal.appendChild(this.colorPickerBody);

    this.colorPickerFooter = this.hot.rootDocument.createElement('div');
    this.colorPickerFooter.classList.add('color-picker-footer');
    this.colorPickerModal.appendChild(this.colorPickerFooter);

    this.iconSvg = this.hot.rootDocument.createElementNS('http://www.w3.org/2000/svg', 'svg');
    this.iconSvg.setAttribute('viewBox', '0 0 32 32');
    this.iconSvg.setAttribute('height', '24');
    this.iconSvg.setAttribute('width', '24');
    this.iconSvg.classList.add('icon', 'pencil-icon');

    this.iconPath = this.hot.rootDocument.createElementNS('http://www.w3.org/2000/svg', 'path');
    this.iconPath.setAttribute('d', Icons['pencil']);
    this.iconSvg.appendChild(this.iconPath);
    this.colorPickerHeader.appendChild(this.iconSvg);

    this.colorHexInput = this.hot.rootDocument.createElement('input');
    this.colorHexInput.classList.add('color-picker-hex-input');
    this.colorHexInput.id = 'color-picker-hex-input';
    this.colorHexInput.maxLength = 7;
    this.colorHexInput.spellcheck = false;
    this.colorHexInput.setAttribute('aria-label', 'Hex color');
    this.colorHexInput.setAttribute('aria-invalid', 'false');
    this.colorHexInput.setAttribute('data-test-element', 'hex-input');
    this.colorPickerHeader.appendChild(this.colorHexInput);

    this.colorHexError = this.hot.rootDocument.createElement('p');
    this.colorHexError.id = 'color-picker-hex-error';
    this.colorHexError.classList.add('color-picker-hex-error');
    this.colorHexError.hidden = true;
    this.colorHexError.setAttribute('role', 'alert');
    this.colorHexError.setAttribute('data-test-element', 'hex-error');
    this.colorHexError.textContent = 'Enter a hex color like #RGB or #RRGGBB';
    this.colorPickerHeader.appendChild(this.colorHexError);

    this.colorChip = this.hot.rootDocument.createElement('span');
    this.colorChip.classList.add('color-picker-chip');
    this.colorPickerHeader.appendChild(this.colorChip);

    this.saveButton = this.hot.rootDocument.createElement('button');
    this.saveButton.classList.add('color-picker-save');
    this.saveButton.innerText = 'Save';
    this.saveButton.setAttribute('data-test-element', 'save-button');
    this.colorPickerFooter.appendChild(this.saveButton);

    this.cancelButton = this.hot.rootDocument.createElement('button');
    this.cancelButton.classList.add('color-picker-cancel', 'plain', 'ml2');
    this.cancelButton.innerText = 'Cancel';
    this.colorPickerFooter.appendChild(this.cancelButton);

    this.hot.rootDocument.body.appendChild(this.colorPickerContainer);

    this.colorPickerModal.addEventListener('mousedown', (e) => e.stopPropagation());
    this.colorPickerContainer.addEventListener('mousedown', () => this.finishEditing(false));

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.colorPicker = new (iro.ColorPicker as any)(this.colorPickerBody!, {
      color: '#ffffff',
      layoutDirection: 'horizontal',
      width: 200,
      layout: [{ component: iro.ui.Box }, { component: iro.ui.Slider, options: { sliderType: 'hue' } }],
    });

    if (_notNil(this.colorPicker)) {
      this.colorPicker.on('color:change', (color: iro.Color) => this.setValue(color.hexString));
    }

    this.colorHexInput.addEventListener('input', (event) => {
      const value = (event.target as HTMLInputElement)?.value ?? '';
      const normalized = value.startsWith('#') ? value : `#${value}`;
      if (validHexColorPattern.test(normalized)) {
        this.setValue(normalized);
        return;
      }
      this.setHexInputValidity(false);
    });

    this.saveButton!.addEventListener('click', () => this.finishEditing(false));
    this.cancelButton!.addEventListener('click', () => this.finishEditing(true));
    this.colorChip!.addEventListener('click', () => this.setValue(ColorEditor.randomSwatchColor()));
  }

  setValue(newValue: string): void {
    const color = newValue ?? ColorEditor.randomSwatchColor();
    const isValidHex = validHexColorPattern.test(color);

    super.setValue(color);

    // iro accepts only 3, 4, 6, or 8 hex digits. A 5- or 7-digit edit (for example
    // deleting one character from #RRGGBB) still matches iro's loose prefix check,
    // so color.set throws "Invalid hex string". That throw is unhandled when
    // Handsontable refreshValue pushes the saved draft back into this editor.
    if (isValidHex && _notNil(this.colorPicker) && this.colorPicker.color.hexString !== color) {
      this.colorPicker.color.set(color);
    }

    if (_notNil(this.colorHexInput) && this.colorHexInput.value !== color) {
      this.colorHexInput.value = color;
    }

    this.setHexInputValidity(isValidHex);

    if (!isValidHex) {
      return;
    }

    if (_notNil(this.TEXTAREA) && this.TEXTAREA instanceof HTMLTextAreaElement) {
      if (this.TEXTAREA.value !== color) {
        this.TEXTAREA.value = color;
        this.TEXTAREA.style.color = color;
        this.TEXTAREA.style.backgroundColor = color;
      }
    }

    if (_notNil(this.colorChip)) {
      this.colorChip.style.backgroundColor = color;
    }
  }

  /**
   * Marks the hex field invalid while the draft is not #RGB or #RRGGBB,
   * so a partial edit is visible before the cell is saved.
   * The error is linked with aria-describedby only while invalid, because a
   * hidden description can still be announced.
   */
  private setHexInputValidity(isValid: boolean): void {
    if (_notNil(this.colorHexInput)) {
      this.colorHexInput.classList.toggle('invalid', !isValid);
      this.colorHexInput.setAttribute('aria-invalid', isValid ? 'false' : 'true');
      if (isValid) {
        this.colorHexInput.removeAttribute('aria-describedby');
      } else {
        this.colorHexInput.setAttribute('aria-describedby', 'color-picker-hex-error');
      }
    }

    if (_notNil(this.colorHexError)) {
      this.colorHexError.hidden = isValid;
    }
  }

  getValue(): string {
    if (!_notNil(this.colorHexInput)) {
      return '';
    }
    return this.colorHexInput.value ?? '';
  }

  destroyElements(): void {
    this._isDestroyed = true;
    if (_notNil(this.colorPickerContainer)) {
      while (this.colorPickerContainer.firstChild) {
        this.colorPickerContainer.removeChild(this.colorPickerContainer.firstChild);
      }
      this.colorPickerContainer.remove();
      this.colorPickerContainer = undefined;
    }
  }

  /**
   * Open/close is driven entirely by our own `_isOpened` flag rather than the
   * base editor's `state` machine, which only reopens when `state === VIRGIN`
   * and depends on an async validation hook to reset it. When that hook failed
   * to fire after saving an invalid value (an un-reproducible timing race), the
   * cell became permanently un-editable; this keeps reopening self-healing.
   */
  beginEditing(): void {
    this.open();
  }

  finishEditing(restoreOriginalValue = false, ctrlDown = false): void {
    if (this._isDestroyed) {
      return;
    }

    if (!this._isOpened) {
      try {
        super.finishEditing(restoreOriginalValue, ctrlDown);
      } catch {
        // Instance may already be destroyed.
      }
      return;
    }

    try {
      if (!restoreOriginalValue) {
        const currentValue = this.getValue();
        if (currentValue !== this.originalValue && _notNil(this.row) && _notNil(this.col)) {
          this.hot.setDataAtCell(this.row, this.col, currentValue);
        }
      }
    } catch {
      // Ignore: teardown below still runs so the editor can reopen.
    } finally {
      // finally guarantees teardown even if save/validation throws, so an
      // invalid value can never leave the picker wedged open (stuck-editor bug).
      this.close();
      try {
        super.finishEditing(restoreOriginalValue, ctrlDown);
      } catch {
        // Instance may already be destroyed.
      }
      if (!this._isDestroyed) {
        this.hot.listen();
      }
    }
  }

  open(event: Event | null = null): void {
    if (this._isDestroyed) {
      return;
    }

    if (!_notNil(this.colorPickerContainer)) {
      this.createElements();
    }

    this._isOpened = true;
    this.showColorPicker(event);
  }

  close(): void {
    if (!this._isOpened) {
      return;
    }
    this._isOpened = false;
    this.hideColorPicker();

    if (!this._isDestroyed) {
      this.hot.render();
    }
  }

  isOpened(): boolean {
    return this._isOpened;
  }

  focus(): void {
    this.colorHexInput?.focus();
  }

  showColorPicker(_event: Event | null): void {
    const valueToSet = this._colorValue ?? (this.originalValue as string);
    this.setValue(valueToSet);

    if (_notNil(this.colorPickerContainer)) {
      this.colorPickerContainer.style.display = 'block';
    }
    this.positionColorPicker();
  }

  /**
   * Places the picker at the cell, kept inside the viewport, so the Save button of a picker opened on a
   * low row is not pushed off screen. With no room below the cell it opens above it rather than over it:
   * the editor opens between the clicks of a double click, so a Save button left under the pointer would
   * take the second click and close the picker straight away.
   */
  private positionColorPicker(): void {
    if (_isNil(this.colorPickerModal) || _isNil(this.TD)) {
      return;
    }
    const cell = this.TD.getBoundingClientRect();
    const { offsetWidth: width, offsetHeight: height } = this.colorPickerModal;
    const { innerWidth: viewportWidth, innerHeight: viewportHeight } = this.hot.rootWindow;

    let top = cell.top;
    if (cell.top + height > viewportHeight) {
      top = cell.top - height >= 0 ? cell.top - height : Math.max(0, viewportHeight - height);
    }
    const left = Math.max(0, Math.min(cell.left, viewportWidth - width));

    this.colorPickerModal.style.top = `${top}px`;
    this.colorPickerModal.style.left = `${left}px`;
  }

  hideColorPicker(): void {
    if (_notNil(this.colorPickerContainer)) {
      this.colorPickerContainer.style.display = 'none';
    }
  }
}
